import React, { PureComponent } from 'react';
import {
    CapacityModel,
    CountsAlgorithm,
    CountType,
    ErrorResponse,
    HotspotDto,
    HotspotKind,
    HotspotListRequestDto,
    HotspotPlanDto,
    HotspotSeverity,
    HotspotsForDuration,
    HotspotStatus,
    MeasureType,
    MonitoredLoadValue,
    MonitoredTv,
    RegulationDto,
    StandardCountSubtotalsType,
    TimePeriod,
    TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
    TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
    TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
    TrafficCountsByTvRequestDto,
    TrafficCountsDto,
    TrafficMonitorRequest,
    TrafficType,
    TrafficVolumeListRequest_IdType,
    TvRefLocation,
} from 'app/model/CORE';
import { Flex } from 'app/components/common/Flex';
import './HotspotPanel.less';
import ConditionalRender from 'app/common/components/ConditionalRender';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';
import AppContext from 'app/common/context/AppContext';
import { ServiceFactory } from 'app/service/ServiceFactory';
import { Button, Col, Divider, Input, notification, Radio, Row, Select, Space, Spin } from 'antd';
import SelectEnum from 'app/common/input/SelectEnum';
import { CountsGraph } from 'app/components/panels/tv/graph/CountsGraph';
import TrafficCountUtils from 'app/common/util/TrafficCountUtils';
import memoizeOne from 'memoize-one';
import MonitoredLoadUtils from 'app/common/util/MonitoredLoadUtils';
import { OccupancyLoadBar } from 'app/components/dashboard/network/flows/svg/OccupancyLoadBar';
import { ModalPanelProps } from 'app/components/panels/DashboardModal';
import InputTimePeriod from 'app/common/input/InputTimePeriod';
import { CloseCircleOutlined, LoadingOutlined, PlusCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import TimePeriodUtils from 'app/common/util/TimePeriodUtils';
import CapacityUtils from 'app/common/util/CapacityUtils';
import { EntryLoadBar } from 'app/components/dashboard/network/flows/svg/EntryLoadBar';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { ModalHandler } from 'app/components/panels/ModalHandler';

const DEFAULT_HOTSPOT_DURATION = 1;

const EXCLUDED_STATUS_PROTECTION_HOTSPOT = [HotspotStatus.DRAFT, HotspotStatus.ACCEPTABLE, HotspotStatus.SOLVED];

const EMPTY_HOTSPOT = (tv: string, duration: number, refLoc: TvRefLocation, timePeriod: TimePeriod): HotspotDto => ({
    hotspotId: {
        timePeriod: timePeriod,
        durationInMinutes: duration,
        tvId: tv,
    },
    location: refLoc,
});

interface HotspotPanelProps extends ModalPanelProps {
    tv: string;
    refLoc?: TvRefLocation;
    duration?: number;
}

interface HotspotPanelState {
    tv: string;
    refLoc: TvRefLocation;
    hotspotPlan?: HotspotPlanDto;
    isCreate: boolean;
    duration?: number;
    lastUpdate?: Date;
    monitoredTv?: MonitoredTv;
    entryCounts?: TrafficCountsDto;
    occCounts?: TrafficCountsDto;
    countType: CountType;
    timePeriod: TimePeriod;
    loading: boolean;
    outdated: boolean;

    // Counts settings
    entryInterval: number;
    entryStep: number;
    entryTrafficType: TrafficType;
    occTrafficType: TrafficType;
}

export class HotspotPanel extends PureComponent<HotspotPanelProps, HotspotPanelState> {
    static HOTSPOT_PANEL_WIDTH_EM = '80em';
    private tvIdChangeTimer: NodeJS.Timeout;

    constructor(props) {
        super(props);

        this.state = {
            countType: CountType.ENTRY,
            timePeriod: AppContext.getMonitoringPeriod(),
            isCreate: true,
            tv: this.props.tv,
            refLoc: this.props.refLoc,
            duration: this.props.duration || DEFAULT_HOTSPOT_DURATION,
            loading: false,
            outdated: false,
            entryInterval: 20,
            entryStep: 60,
            entryTrafficType: TrafficType.LOAD,
            occTrafficType: TrafficType.LOAD,
        };
    }

    componentDidMount() {
        this.refresh();
    }

    componentDidUpdate(prevProps: Readonly<HotspotPanelProps>, prevState: Readonly<HotspotPanelState>, snapshot?: any) {
        if (this.state.tv !== prevState.tv) {
            // Prevent multiple refresh until user stops typing (timeout of 1s)
            clearTimeout(this.tvIdChangeTimer);
            this.tvIdChangeTimer = setTimeout(() => {
                this.refresh();
            }, 1000);
        }
    }

    refresh = () => {
        if (!this.state.tv) {
            this.setState({
                refLoc: undefined,
                monitoredTv: undefined,
                entryCounts: undefined,
                occCounts: undefined,
            });
            return;
        }

        this.setState({
            loading: true,
        });
        // Retrieve TV to make sure TvId exists before proceeding
        ServiceFactory.coreService()
            .getTrafficVolumeList({
                wildcardIds: [this.state.tv],
                dataset: AppContext.getDataset(),
                idType: TrafficVolumeListRequest_IdType.TV,
            })
            .then(reply => {
                if (Utils.size(reply.data) === 1) {
                    this.setState({
                        tv: reply.data[0].id,
                        refLoc: reply.data[0].refLocation,
                    });
                    return this.refreshHotspot();
                } else {
                    this.setState({
                        refLoc: undefined,
                    });
                    return undefined;
                }
            })
            .then(hotspotPlansReply => {
                // TV doesn't exist
                if (!hotspotPlansReply) return [undefined, undefined, undefined];

                const hotspotPlan = hotspotPlansReply?.data;
                if (!hotspotPlan) {
                    throw new Error(
                        `Failed to fetch hotspot plan: ${hotspotPlansReply.error.message}, ${hotspotPlansReply.error.details}`,
                    );
                }
                // Sort hotspots
                hotspotPlan.hotspotsForDurations?.forEach(hfd => this.sortHotspots(hfd.hotspots));

                const timePeriod = this.state.timePeriod;
                const hotspotPeriod = TimePeriodUtils.coversAll(
                    this.getHotspotsForDuration(hotspotPlan, this.state.duration)?.map(
                        hotspot => hotspot.hotspotId.timePeriod,
                    ),
                );
                if (hotspotPeriod != null) {
                    // Get hotspot period extended by 1H at both ends, and covering Monitoring Period
                    TimePeriodUtils.expandBy(hotspotPeriod, TimeUtils.HR_TO_MS, TimeUtils.HR_TO_MS);
                    TimePeriodUtils.growToCover(hotspotPeriod, timePeriod);
                }

                this.setState({ hotspotPlan, isCreate: Utils.isEmpty(hotspotPlan?.hotspotsForDurations), timePeriod });
                return Promise.all([
                    this.refreshEntryCounts(timePeriod),
                    this.refreshOccupancyCounts(timePeriod),
                    this.refreshTrafficMonitorData(timePeriod),
                ]);
            })
            .then(([entryCountsReply, occCountsReply, monitoredTvReply]) => {
                this.setState({
                    entryCounts: entryCountsReply?.data,
                    occCounts: occCountsReply?.data,
                    monitoredTv: monitoredTvReply?.data?.monitoredTvSets?.[0].monitoredTvs?.[0],
                    lastUpdate: AppContext.getCurrentTime(),
                });
            })
            .catch(error => {
                notification.error({ message: 'Error during refresh', description: '' + error, duration: 0 });
                console.log('Error during refresh: ', error);
            })
            .finally(() => this.setState({ loading: false }));
    };

    refreshHotspot = () => {
        const request: HotspotListRequestDto = {
            dataset: AppContext.getDataset(),
            tvId: this.state.tv,
            day: AppContext.getMonitoredDay(),
        };
        return ServiceFactory.stamCoreService().getHotspotPlan(request);
    };

    refreshEntryCounts = (tp: TimePeriod) => {
        const req: TrafficCountsByTvRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [TrafficType.LOAD, TrafficType.DEMAND, TrafficType.REGULATED_DEMAND],
            trafficWindow: tp,
            algorithm: CountsAlgorithm.PSEUDO_NM,
            countType: CountType.ENTRY,
            durationInMin: this.state.entryStep,
            intervalInMin: this.state.entryInterval,
            includeForecastFlights: true,
            subtotalsType: StandardCountSubtotalsType.NM,
            tvId: this.state.tv,
            computeHourlyEntryRates: true,
            computeHotspots: false,
        };
        return ServiceFactory.coreService().getTrafficCounts(req);
    };

    refreshOccupancyCounts = (tp: TimePeriod) => {
        const request: TrafficCountsByTvRequestDto = {
            clazz: TRAFFIC_COUNTS_BY_TV_REQUEST_DTO_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            trafficTypes: [TrafficType.LOAD, TrafficType.DEMAND, TrafficType.REGULATED_DEMAND],
            trafficWindow: tp,
            algorithm: CountsAlgorithm.PSEUDO_NM,
            countType: CountType.OCCUPANCY,
            durationInMin: this.state.duration,
            intervalInMin: 1,
            includeForecastFlights: true,
            subtotalsType: StandardCountSubtotalsType.NM,
            tvId: this.state.tv,
            computeOtmvDefns: true,
            computeHotspots: false,
        };
        return ServiceFactory.coreService().getTrafficCounts(request);
    };

    refreshTrafficMonitorData = (tp: TimePeriod) => {
        this.setState({ loading: true });
        const req: TrafficMonitorRequest = {
            clazz: TRAFFIC_MONITOR_REQUEST_CLAZZ_VALUE,
            dataset: AppContext.getDataset(),
            proposalContexts: null,
            adhocTvList: [this.state.tv],
            capacityModel: AppContext.getCapacityModel(),
            entryCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            occupancyCountsAlgorithm: CountsAlgorithm.PSEUDO_NM,
            timePeriod: tp,
            includeForecastFlights: true,
        };
        return ServiceFactory.coreService().getTrafficMonitorData(req);
    };

    render() {
        const hotspots = this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration);
        const hotspotsPeriods = this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration)?.map(
            hotspot => hotspot.hotspotId.timePeriod,
        );
        return (
            <Spin spinning={this.state.loading}>
                <Flex direction="vertical" className="hotspot-panel">
                    <Flex className="modal-title header" style={{ alignItems: 'center' }}>
                        <ConditionalRender condition={Utils.isExists(this.state.lastUpdate)}>
                            <Flex className="update-time" size="15em">
                                last update{' '}
                                {TimeUtils.formatDateDDMMYYYY(this.state.lastUpdate, '-') +
                                    ' ' +
                                    TimeUtils.formatHHMM(this.state.lastUpdate)}
                            </Flex>
                        </ConditionalRender>
                        <Flex direction="vertical">
                            <Flex className="title">{`HOTSPOT - TFV ${this.state.tv}`}</Flex>
                        </Flex>
                        <Flex style={{ justifyContent: 'flex-end' }}>
                            <ConditionalRender condition={this.state.loading}>
                                <LoadingOutlined style={{ fontSize: '2em', paddingRight: '1em' }} />
                            </ConditionalRender>
                            <ConditionalRender condition={!this.state.loading}>
                                <ReloadOutlined
                                    title="Refresh"
                                    onClick={this.refresh}
                                    style={{ fontSize: '2em', cursor: 'pointer', paddingRight: '1em' }}
                                />
                            </ConditionalRender>
                        </Flex>
                    </Flex>
                    <Divider type="horizontal" />
                    <Flex size="20em" direction="vertical">
                        <div style={{ paddingLeft: '3em' }}>
                            <Radio.Group
                                value={this.state.countType}
                                onChange={value => this.setState({ countType: value.target.value })}>
                                <Radio.Button
                                    style={{ width: '8em', textAlign: 'center' }}
                                    disabled={!Utils.isExists(this.state.entryCounts)}
                                    value={CountType.ENTRY}>
                                    Entry
                                </Radio.Button>
                                <Radio.Button
                                    style={{ width: '8em', textAlign: 'center' }}
                                    disabled={!Utils.isExists(this.state.occCounts)}
                                    value={CountType.OCCUPANCY}>
                                    Occupancy
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        {this.state.countType === CountType.ENTRY && this.renderEntry(hotspotsPeriods)}
                        {this.state.countType === CountType.OCCUPANCY && this.renderOccupancy(hotspotsPeriods)}
                    </Flex>
                    <Divider type="horizontal" />
                    <div className="info">
                        <Row gutter={[8, 8]}>
                            <Col span={4}>
                                Traffic Volume
                                <Input value={this.state.tv} onChange={e => this.onTvChange(e.target.value)} />
                            </Col>
                            <Col span={4}>
                                Ref Loc
                                <Input value={this.state.refLoc?.refId} disabled={true} />
                            </Col>
                        </Row>
                        {hotspots?.map((hotspot, idx) => (
                            <Row gutter={[8, 8]} key={idx}>
                                <Col flex={'auto'}>
                                    {`${idx === 0 ? 'Periods' : ''}`}
                                    <InputTimePeriod
                                        referenceDay={AppContext.getMonitoredDay()}
                                        value={hotspot?.hotspotId?.timePeriod}
                                        onChange={v => this.onTimePeriodChange(v, hotspot, idx)}
                                    />
                                </Col>
                                <Col flex={'auto'} style={{ display: 'flex', flexDirection: 'column' }}>
                                    {`${idx === 0 ? 'Hotspot Kind' : ''}`}
                                    <SelectEnum
                                        className="hotspot-kind"
                                        enumType={HotspotKind}
                                        value={hotspot?.kind}
                                        excludeValues={AppContext.isNmvpBaseline() ? [] : [HotspotKind.PROTECTION]}
                                        onChange={(v: HotspotKind) => this.onKindChange(v, idx)}
                                    />
                                </Col>
                                <Col flex={'auto'} style={{ display: 'flex', flexDirection: 'column' }}>
                                    {`${idx === 0 ? 'Hotspot Severity' : ''}`}
                                    <SelectEnum
                                        className="hotspot-severity"
                                        enumType={HotspotSeverity}
                                        excludeValues={[HotspotSeverity.NONE_SEVERITY]}
                                        disabled={hotspot?.kind === HotspotKind.PROTECTION}
                                        value={hotspot?.severity}
                                        onChange={(v: HotspotSeverity) => this.onSeverityChange(v, idx)}
                                    />
                                </Col>
                                <Col flex={'auto'} style={{ display: 'flex', flexDirection: 'column' }}>
                                    {`${idx === 0 ? 'Hotspot Status' : ''}`}
                                    <SelectEnum
                                        className="hotspot-status"
                                        enumType={HotspotStatus}
                                        excludeValues={
                                            hotspot?.kind === HotspotKind.PROTECTION
                                                ? EXCLUDED_STATUS_PROTECTION_HOTSPOT
                                                : []
                                        }
                                        value={hotspot?.status}
                                        onChange={(v: HotspotStatus) => this.onStatusChange(v, idx)}
                                    />
                                </Col>
                                <Col flex={0} style={{ display: 'flex', flexDirection: 'column' }}>
                                    {`${idx === 0 ? 'Remark' : ''}`}
                                    <Input
                                        className="remark"
                                        value={hotspot?.remark}
                                        onChange={e => this.onRemarkChange(e.target.value, idx)}
                                    />
                                </Col>
                                <Col
                                    span={1}
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}>
                                    {idx === 0 && <br />}
                                    <CloseCircleOutlined
                                        className="remove-period"
                                        onClick={() => this.removePeriod(idx)}
                                    />
                                </Col>
                            </Row>
                        ))}
                        <Row gutter={[8, 8]}>
                            <Col
                                span={1}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}>
                                <br />
                                <PlusCircleOutlined className="add-period" onClick={() => this.addNewPeriod()} />
                            </Col>
                        </Row>
                    </div>
                    <Flex className="buttons">
                        <Space>
                            <Button key="close" onClick={this.props.onClose}>
                                Close
                            </Button>
                            <Button
                                key="show-on-map"
                                disabled={Utils.isEmptyString(this.state.tv)}
                                onClick={() => ModalHandler.showMapPanel([], [this.state.tv])}>
                                Show on map
                            </Button>
                            <Button
                                key="create-regulation"
                                disabled={!Utils.isExists(this.state.monitoredTv)}
                                onClick={this.onCreateRegulation}>
                                New regulation
                            </Button>
                            <Button
                                key="update"
                                disabled={
                                    AppContext.isOpsBaseline() ||
                                    !this.state.outdated ||
                                    !this.hasValidHotspotsForDuration()
                                }
                                onClick={() =>
                                    this.onUpdateHotspotPlan()
                                }>{`${this.state.isCreate ? 'Create' : 'Update'} hotspot`}</Button>
                        </Space>
                    </Flex>
                </Flex>
            </Spin>
        );
    }

    private onCreateRegulation = () => {
        const hotspotsPeriods = this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration)?.map(
            hotspot => hotspot.hotspotId.timePeriod,
        );
        const initialConstraints = hotspotsPeriods?.flatMap(timePeriod =>
            MeasureUtils.hourlyEntryCapacityToRegInitialConstraints(
                this.state.entryCounts?.hourlyEntryCapacity,
                timePeriod,
            ),
        );
        const occupancyConstraints = hotspotsPeriods?.flatMap(timePeriod =>
            MeasureUtils.otmvsToRegOccupancyConstraints(this.state.occCounts?.otmvDefns, timePeriod),
        );

        const initialRegulation: Partial<RegulationDto> = {
            applicability: TimePeriodUtils.coversAll(hotspotsPeriods),
            location: {
                id: this.state.tv,
                clazz: TRAFFIC_VOLUME_LOCATION_DTO_CLAZZ_VALUE,
            },
            measureId: {
                id: MeasureUtils.getRegulationId(this.state.tv),
                type: MeasureType.REGULATION,
            },
            initialConstraints: initialConstraints,
            occupancyConstraints: occupancyConstraints,
            calculationType: this.state.countType,
            occupancyDurationInMs: this.state.duration * TimeUtils.MIN_TO_MS,
        };
        ModalHandler.showRegulationEditor(this.state.tv, 'tv', initialRegulation);
    };

    private onUpdateHotspotPlan() {
        // Should send updates for 1 duration only (NM restriction)
        const updatedHotspotPlan: HotspotPlanDto = {
            ...this.state.hotspotPlan,
            hotspotsForDurations: [
                this.state.hotspotPlan.hotspotsForDurations.find(hfd => hfd.durationInMinutes === this.state.duration),
            ],
        };
        this.setState({
            loading: true,
        });
        ServiceFactory.stamCoreService()
            .updateHotspotPlan(updatedHotspotPlan)
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: reply.error?.message, description: reply.error?.details });
                } else {
                    // Sort hotspots
                    reply.data?.hotspotsForDurations?.forEach(hfd => this.sortHotspots(hfd.hotspots));
                    this.setState({
                        hotspotPlan: reply.data,
                        isCreate: Utils.isEmpty(reply.data?.hotspotsForDurations),
                        outdated: false,
                    });
                }
            })
            .catch((error: ErrorResponse) =>
                notification.error({
                    message: 'Error updating/creating hotspot',
                    description: '' + error,
                }),
            )
            .finally(() => this.setState({ loading: false }));
    }

    renderEntryLoad = () => {
        const monitoredPeriod = this.state.entryCounts?.timeRange;
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    E
                </Flex>
                <Flex direction="vertical">
                    <EntryLoadBar
                        loadValues={this.state.monitoredTv?.loadValues}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                </Flex>
            </Flex>
        );
    };
    private renderEntry = (hotspotsPeriods: TimePeriod[]) => {
        const hourlyEntryCapacity =
            AppContext.getCapacityModel() === CapacityModel.WCM_NRP
                ? CapacityUtils.getWcmCapacity(
                      this.state.entryCounts?.hourlyEntryCapacity,
                      this.state.entryCounts?.timeRange,
                  )
                : this.state.entryCounts?.hourlyEntryCapacity;
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">
                        <CountsGraph
                            currentTime={AppContext.getCurrentTime()}
                            counts={
                                this.state.entryCounts &&
                                TrafficCountUtils.getCountsFor(this.state.entryCounts, this.state.entryTrafficType)
                            }
                            activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                            timeRange={this.state.entryCounts?.timeRange}
                            hourlyEntryCapacity={hourlyEntryCapacity}
                            hotspotsPeriods={hotspotsPeriods}
                            onSelectedPeriodChange={this.addNewPeriod}
                        />
                    </Flex>
                    <Flex size="1em">{this.renderEntryLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <Space direction="vertical">
                        <Select value="by_type" style={{ width: '100%' }}>
                            <Select.Option value="by_type">By type</Select.Option>
                            <Select.Option disabled={true} value="by_flow">
                                By flow
                            </Select.Option>
                        </Select>
                        <Select
                            value={this.state.entryTrafficType}
                            onChange={tt => this.setState({ entryTrafficType: tt })}
                            style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                            <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                            <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                        </Select>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                Interval
                                <Select
                                    value={this.state.entryInterval}
                                    onChange={value =>
                                        this.setState({ entryInterval: value }, this.onEntrySettingsChange)
                                    }
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Col>
                            <Col span={12}>
                                Step
                                <Select
                                    value={this.state.entryStep}
                                    onChange={value => this.setState({ entryStep: value }, this.onEntrySettingsChange)}
                                    style={{ width: '100%' }}>
                                    <Select.Option value={5}>5</Select.Option>
                                    <Select.Option value={10}>10</Select.Option>
                                    <Select.Option value={20}>20</Select.Option>
                                    <Select.Option value={60}>60</Select.Option>
                                </Select>
                            </Col>
                        </Row>
                    </Space>
                </Flex>
            </Flex>
        );
    };

    private renderOccupancy = (hotspotsPeriods: TimePeriod[]) => {
        const durationsWithHotspots = this.state.hotspotPlan?.hotspotsForDurations?.map(hfd => hfd.durationInMinutes);
        return (
            <Flex>
                <Flex direction="vertical" style={{ paddingTop: '1em' }}>
                    <Flex size="20em">
                        <CountsGraph
                            currentTime={AppContext.getCurrentTime()}
                            counts={
                                this.state.occCounts &&
                                TrafficCountUtils.getCountsFor(this.state.occCounts, this.state.occTrafficType)
                            }
                            activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                            timeRange={this.state.occCounts?.timeRange}
                            otmvDefns={this.state.occCounts?.otmvDefns}
                            hotspotsPeriods={hotspotsPeriods}
                            onSelectedPeriodChange={this.addNewPeriod}
                        />
                    </Flex>
                    <Flex size="1em">{this.renderOccupancyLoad()}</Flex>
                </Flex>
                <Flex direction="vertical" size="10em" style={{ paddingLeft: '1em' }}>
                    <Space direction="vertical">
                        <Select value="by_type" style={{ width: '100%' }}>
                            <Select.Option value="by_type">by type</Select.Option>
                        </Select>
                        <Select
                            value={this.state.occTrafficType}
                            onChange={tt => this.setState({ occTrafficType: tt })}
                            style={{ width: '100%' }}>
                            <Select.Option value={TrafficType.LOAD}>Load</Select.Option>
                            <Select.Option value={TrafficType.DEMAND}>Demand</Select.Option>
                            <Select.Option value={TrafficType.REGULATED_DEMAND}>Regulated demand</Select.Option>
                        </Select>
                        <Select value={this.state.duration} onChange={this.onDurationChange}>
                            {Array.from(Array(20).keys()).map(duration => {
                                return (
                                    <Select.Option
                                        key={`duration-${duration + 1}`}
                                        value={
                                            duration + 1
                                        }>{`${duration + 1} min ${durationsWithHotspots?.includes(duration + 1) ? '*' : ''}`}</Select.Option>
                                );
                            })}
                        </Select>
                    </Space>
                </Flex>
            </Flex>
        );
    };

    renderOccupancyLoad = () => {
        const occLoad =
            (this.state.monitoredTv?.occupancyLoadValues?.length > 0 &&
                this.state.monitoredTv?.occupancyLoadValues[0].occupancyLoadValues) ||
            [];
        const monitoredPeriod = this.state.occCounts?.timeRange;
        return (
            <Flex>
                <Flex
                    size="30px"
                    style={{
                        fontWeight: 'bold',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: '5px',
                    }}>
                    O
                </Flex>
                <Flex direction="vertical">
                    <OccupancyLoadBar
                        loadValues={occLoad}
                        monitoredPeriod={monitoredPeriod}
                        activePeriods={this.getActivePeriods(this.state.monitoredTv?.loadValues)}
                        barHeightEm={2}
                        hideLabel={true}
                        drawBottomLine={true}
                    />
                </Flex>
            </Flex>
        );
    };

    private getHotspotsForDuration = memoizeOne((hotspotPlan: HotspotPlanDto, duration: number) => {
        return hotspotPlan?.hotspotsForDurations?.find(hfd => hfd.durationInMinutes === duration)?.hotspots;
    });

    private getActivePeriods = memoizeOne((monitoredLoadValues: Array<MonitoredLoadValue>) => {
        return monitoredLoadValues && MonitoredLoadUtils.getActivePeriods(monitoredLoadValues);
    });

    private onEntrySettingsChange = () => {
        this.setState({ loading: true });
        this.refreshEntryCounts(this.state.timePeriod)
            .then(reply => {
                this.setState({ entryCounts: reply?.data });
            })
            .finally(() => this.setState({ loading: false }));
    };

    private onTvChange = (tv: string) => this.setState({ tv: tv.toUpperCase() });

    private onDurationChange = (duration: number) => this.setState({ duration }, () => this.refresh());

    private onTimePeriodChange = (timePeriod: TimePeriod, hotspot: HotspotDto, idx: number) => {
        this.onHotspotUpdate({ hotspotId: { ...hotspot.hotspotId, timePeriod } }, idx);
    };

    private onSeverityChange = (severity: HotspotSeverity, idx: number) => this.onHotspotUpdate({ severity }, idx);

    private onKindChange = (kind: HotspotKind, idx: number) => this.onHotspotUpdate({ kind }, idx);

    private onStatusChange = (status: HotspotStatus, idx: number) => this.onHotspotUpdate({ status }, idx);

    private onRemarkChange = (remark: string, idx: number) => this.onHotspotUpdate({ remark }, idx);

    private addNewPeriod = (timePeriod?: TimePeriod) => {
        const newHotspot: HotspotDto = EMPTY_HOTSPOT(this.state.tv, this.state.duration, this.state.refLoc, timePeriod);
        let hotspotsForDurations: HotspotsForDuration[];
        if (this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration)) {
            // Update hotspots list for this duration
            hotspotsForDurations = this.state.hotspotPlan?.hotspotsForDurations?.map(hfd =>
                hfd.durationInMinutes !== this.state.duration
                    ? hfd
                    : {
                          ...hfd,
                          hotspots: [...hfd.hotspots, newHotspot],
                      },
            );
        } else {
            // Insert new hotspot list for this duration
            const hfd = this.state.hotspotPlan.hotspotsForDurations || [];
            hotspotsForDurations = [...hfd, { durationInMinutes: this.state.duration, hotspots: [newHotspot] }];
        }
        this.setState({
            outdated: true,
            hotspotPlan: { ...this.state.hotspotPlan, hotspotsForDurations },
        });
    };

    private removePeriod(idxToRemove: number) {
        const hfd = this.state.hotspotPlan?.hotspotsForDurations?.map(hfd => {
            return hfd.durationInMinutes !== this.state.duration ? hfd : this.removeHotspots(hfd, idxToRemove);
        });
        this.setState({
            outdated: true,
            hotspotPlan: { ...this.state.hotspotPlan, hotspotsForDurations: hfd },
        });
    }

    private removeHotspots(hfd: HotspotsForDuration, idxToRemove: number) {
        const hotspots = hfd.hotspots.filter((_, idx) => idx !== idxToRemove);
        this.sortHotspots(hotspots);
        return { ...hfd, hotspots };
    }

    private onHotspotUpdate = (update: Partial<HotspotDto>, idx: number) => {
        const hotspot = this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration)[idx];
        const updatedHotspot = Object.assign({}, hotspot, update);

        // NMVP constraints
        if (update.kind) {
            this.handleHotspotKindUpdate(update.kind, updatedHotspot);
        }

        const updatedHotspotsForDuration = this.state.hotspotPlan?.hotspotsForDurations?.map(hfd => {
            return hfd.durationInMinutes !== this.state.duration ? hfd : this.updateHotspots(hfd, idx, updatedHotspot);
        });
        this.setState({
            outdated: true,
            hotspotPlan: { ...this.state.hotspotPlan, hotspotsForDurations: updatedHotspotsForDuration },
        });
    };

    private handleHotspotKindUpdate = (kind: HotspotKind, hotspot: HotspotDto) => {
        // NMVP constraints
        if (kind === HotspotKind.PROTECTION) {
            hotspot.severity = HotspotSeverity.NONE_SEVERITY;
            if (Utils.isInList(hotspot.status, ...EXCLUDED_STATUS_PROTECTION_HOTSPOT)) {
                hotspot.status = undefined;
            }
        } else {
            // Reset severity
            hotspot.severity = undefined;
        }
    };

    private updateHotspots = (hfd: HotspotsForDuration, index: number, hotspot: HotspotDto) => {
        const updatedHotspots = hfd.hotspots.map((h, idx) => (idx !== index ? h : hotspot));
        this.sortHotspots(updatedHotspots);
        return { ...hfd, hotspots: updatedHotspots };
    };

    private hasValidHotspotsForDuration = () => {
        const hotspots = this.getHotspotsForDuration(this.state.hotspotPlan, this.state.duration);
        const timePeriods = hotspots?.map(h => h.hotspotId.timePeriod);
        return hotspots?.every(this.isValidHotspot) && !TimePeriodUtils.hasIntersections(timePeriods);
    };

    private isValidHotspot = (hotspot: HotspotDto) => {
        return (
            Utils.isExists(hotspot.severity) &&
            Utils.isExists(hotspot.status) &&
            Utils.isExists(hotspot.kind) &&
            TimePeriodUtils.isValid(hotspot.hotspotId?.timePeriod)
        );
    };

    private sortHotspots = (hotspots: HotspotDto[]) => {
        hotspots.sort((a, b) => {
            const tp1 = a.hotspotId.timePeriod;
            const tp2 = b.hotspotId.timePeriod;
            if (tp1 && TimePeriodUtils.isValid(tp1) && tp2 && TimePeriodUtils.isValid(tp2))
                return TimePeriodUtils.COMPARE_FUNCTION(tp1, tp2);
            else if (tp1 && TimePeriodUtils.isValid(tp1)) return -1;
            else return 1;
        });
    };
}
